import { createEffect, sample } from "effector"
import { AnalyticsService, userApi } from "@callcenter/api"
import type { CallcenterSettings } from "@callcenter/services"
import { StorageService } from "@callcenter/services"
import { $callcenterService, $userService } from "@callcenter/effector-services"

// Keep in sync with libs/authorization authorization.constants.ts
const USER_LOGGED_IN_PENDING_KEY = "user_logged_in_pending"
const SPEECH_RECOGNITION_PERMISSION = "speech_recognition"

const initFx = createEffect(() => AnalyticsService.init({ persistence: "memory" }))

const identifyFx = createEffect((settings: CallcenterSettings) => {
  AnalyticsService.identify({
    callcenterName: settings.name,
    callcenterUuid: settings.uuid,
    accountLevel: settings.account_level
  })
})

// Fires user_logged_in once after a fresh login (flagged by the auth model),
// only for the Feature Requests survey audience: an admin in a callcenter with
// security access groups enabled and speech analytics disabled.
const loginPendingStorage = new StorageService<string>(USER_LOGGED_IN_PENDING_KEY, undefined, sessionStorage)

const reportUserLoggedInFx = createEffect((isSurveyAudience: boolean) => {
  if (loginPendingStorage.getItem() !== "1") {
    return
  }

  loginPendingStorage.remove()

  if (isSurveyAudience) {
    AnalyticsService.sendEvent("user_logged_in")
  }
})

sample({
  clock: $userService.$userSettings,
  filter: ({ is_support }) => !is_support,
  target: initFx
})

sample({
  clock: $callcenterService.$settings,
  target: identifyFx
})

sample({
  clock: userApi.getCallcenterAndUserSettingsFx.doneData,
  fn: ({ user, callcenter, callcenter_permissions }) =>
    user.is_admin &&
    callcenter.security_groups_enabled &&
    !callcenter_permissions.includes(SPEECH_RECOGNITION_PERMISSION),
  target: reportUserLoggedInFx
})
